import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { Observable, Subject } from 'rxjs';

export interface SocketMessage {
  type: string;
  message?: string;
  error?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socketMap = new Map<string, WebSocket>();
  private subjectMap = new Map<string, Subject<SocketMessage>>();
  private reconnectAttempts = new Map<string, number>();
  private connectionStatusMap = new Map<string, Subject<boolean>>();

  private readonly maxReconnectAttempts = 100;
  private readonly reconnectDelay = 2000;

  /** Generate unique key per username + socket type */
  private getKey(username: string, type: string): string {
    return `${username}_${type}`;
  }

  /** Observe connection status (true = open, false = closed) */
  getConnectionStatus(username: string, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): Observable<boolean> {
    const key = this.getKey(username, type);
    if (!this.connectionStatusMap.has(key)) {
      this.connectionStatusMap.set(key, new Subject<boolean>());
    }

    return this.connectionStatusMap.get(key)!.asObservable();
  }

  /** Create or reconnect a WebSocket connection */
  connect(username: string, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): void {
    if (!username) return;
    const key = this.getKey(username, type);

    // Prepare subjects early so `onMessage()` works immediately
    if (!this.subjectMap.has(key)) {
      this.subjectMap.set(key, new Subject<SocketMessage>());
    }
    if (!this.connectionStatusMap.has(key)) {
      this.connectionStatusMap.set(key, new Subject<boolean>());
    }

    const subject = this.subjectMap.get(key)!;
    const statusSubject = this.connectionStatusMap.get(key)!;

    // Select URL based on socket type
    let path = '/ws';
    if (type === 'wsr') {
      path = '/wsr';
    } else if (type === 'ws_scm') {
      path = '/ws_scm';
    }
    else if (type === 'ws_admin') {
      path = '/ws_admin';
    }
    const url = `${environment.socketURL}${path}?username=${encodeURIComponent(username)}`;
    // Skip if already open or connecting
    const existingSocket = this.socketMap.get(key);
    if (
      existingSocket &&
      (existingSocket.readyState === WebSocket.OPEN ||
        existingSocket.readyState === WebSocket.CONNECTING)
    ) {
      // console.log(`[${type}] Already connected or connecting for ${username}`);

      return;
    }

    // console.log(`🔌 Connecting to ${type.toUpperCase()} for ${username} → ${url}`);

    const socket = new WebSocket(url);
    this.socketMap.set(key, socket);

    socket.onopen = () => {
      this.reconnectAttempts.set(key, 0);
      statusSubject.next(true);
      subject.next({ type: 'connected', message: 'WebSocket connected' });
      // eslint-disable-next-line no-console
      // console.log(`✅ ${type.toUpperCase()} connected for ${username}`);
    };

    socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        subject.next(data);
      } catch {
        subject.next({ type: 'text', message: event.data });
      }
      // eslint-disable-next-line no-console
      // console.log(`💬 Message from ${type.toUpperCase()} →`, event.data);
    };

    socket.onclose = () => {
      // console.warn(`⚠️ ${type.toUpperCase()} closed for ${username}`);
      this.socketMap.delete(key);
      statusSubject.next(false);
      subject.next({ type: 'disconnected', message: 'Connection closed' });

      const attempts = this.reconnectAttempts.get(key) ?? 0;
      if (attempts < this.maxReconnectAttempts) {
        this.reconnectAttempts.set(key, attempts + 1);
        // eslint-disable-next-line no-console
        // console.log(
        //   `🔁 Reconnecting ${type} (${attempts + 1}/${this.maxReconnectAttempts}) in ${this.reconnectDelay}ms`
        // );
        setTimeout(() => this.connect(username, type), this.reconnectDelay);
      } else {
        console.error(`[${type}] Max reconnect attempts reached for ${username}`);
        this.reconnectAttempts.delete(key);
      }
    };

    socket.onerror = (error) => {
      console.error(`❌ ${type.toUpperCase()} error for ${username}:`, error);
      statusSubject.next(false);
      subject.next({ type: 'error', error: 'WebSocket error' });

      socket.close(); // triggers onclose and reconnect
    };
  }

  /** Disconnect a specific user's socket */
  disconnect(username: string, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): void {
    const key = this.getKey(username, type);
    const socket = this.socketMap.get(key);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.close();
    }

    this.socketMap.delete(key);
    this.subjectMap.get(key)?.complete();
    this.subjectMap.delete(key);

    const statusSubject = this.connectionStatusMap.get(key);
    if (statusSubject) {
      statusSubject.next(false);
    }

    // console.log(`🔌 ${type.toUpperCase()} disconnected for ${username}`);
  }

  /** Send data through socket */
  send(username: string, data: SocketMessage, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): void {
    const key = this.getKey(username, type);
    const socket = this.socketMap.get(key);
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(data));
      // console.log(`📤 Sent on ${type.toUpperCase()}:`, data);
    } else {
      console.warn(`[${key}] Cannot send — socket not open.`);
    }
  }

  /** Observe incoming messages */
  onMessage(username: string, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): Observable<SocketMessage> | null {
    const key = this.getKey(username, type);
    if (!this.subjectMap.has(key)) {
      this.subjectMap.set(key, new Subject<SocketMessage>());
    }

    return this.subjectMap.get(key)!.asObservable();
  }

  /** Check if user socket is connected */
  isConnected(username: string, type: 'ws' | 'wsr' | 'ws_scm' | 'ws_admin' = 'ws'): boolean {
    const key = this.getKey(username, type);
    const socket = this.socketMap.get(key);

    return socket?.readyState === WebSocket.OPEN;
  }
}
